import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { TabbedContent } from '@/components/TabbedContent';
import { TabBar } from '@/components/TabBar';
import { useTabs } from '@/contexts/TabsContext';
import { PendingBadge } from '@/components/PendingBadge';
import {
  LayoutDashboard,
  Package,
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowRightLeft,
  History,
  Users,
  LogOut,
  Menu,
  X,
  FileText,
  BookOpen,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Settings,
  DatabaseBackup,
  PackageX,
  Building2,
  Cog,
  Briefcase,
  Truck,
  LayoutGrid,
  Shield,
  BotMessageSquare,
  Scale,
  ClipboardCheck,
  ClipboardList,
  FileCheck2,
  Zap,
  FilePlus2,
  FileStack,
  Wrench,
  Warehouse,
  MapPin,
  Trash2,
  Layers,
  PackageSearch,
  PackagePlus,
  Grid3X3,
  Printer,
  AlertTriangle,
  Inbox,
  Receipt,
  BookOpenCheck,
  Wallet,
  MessageCircle,
  Sparkles,
} from 'lucide-react';
import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { openInNewTab } = useTabs();
  // Ctrl / Shift / middle click or a long press opens the page in a new SteelFlow tab
  const pressRef = useRef(null);
  const navProps = (path) => ({
    onClick: (e) => {
      if (pressRef.current?.fired) {
        e.preventDefault();
        pressRef.current = null;
        return;
      }
      if (e.ctrlKey || e.metaKey || e.shiftKey) {
        e.preventDefault();
        openInNewTab(path);
      }
      setSidebarOpen(false);
    },
    onAuxClick: (e) => {
      if (e.button === 1) {
        e.preventDefault();
        openInNewTab(path);
      }
    },
    onTouchStart: () => {
      const st = { fired: false };
      st.timer = setTimeout(() => {
        st.fired = true;
        if (navigator.vibrate) navigator.vibrate(20);
        openInNewTab(path);
        setSidebarOpen(false);
      }, 550);
      pressRef.current = st;
    },
    onTouchEnd: () => clearTimeout(pressRef.current?.timer),
    onTouchMove: () => clearTimeout(pressRef.current?.timer),
    onContextMenu: (e) => {
      if (pressRef.current?.fired) e.preventDefault();
    },
    title: 'Ctrl+click or long-press: open in a new tab',
  });
  const [menuOpen, setMenuOpen] = useState({});

  const isActive = (path) => location.pathname === path;
  const toggleMenu = (key) => setMenuOpen(prev => ({ ...prev, [key]: !prev[key] }));
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  // Main navigation items (unchanged)
  const mainNavItems = [
    { path: '/reports/item-ledger', label: 'Itemwise Ledger', icon: BookOpen },
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/inward', label: 'Inward Entry', icon: ArrowDownToLine, adminOnly: true },
    { path: '/orders', label: 'Orders Inbox', icon: Inbox, adminOnly: true },
    { path: '/outward', label: 'Outward Entry', icon: ArrowUpFromLine, adminOnly: true },
    { path: '/transfer', label: 'Branch Transfer', icon: ArrowRightLeft, adminOnly: true },
    { path: '/history', label: 'Transaction History', icon: History },
    { path: '/challan-register', label: 'Challan Register', icon: FileText, adminOnly: true },
    { path: '/bills', label: 'Bills (Tally)', icon: Receipt, adminOnly: true },
    { path: '/ledger', label: 'Party Ledger', icon: BookOpenCheck, adminOnly: true },
    { path: '/outstanding', label: 'Outstanding', icon: Wallet, adminOnly: true },
    { path: '/whatsapp', label: 'WhatsApp Chats', icon: MessageCircle, adminOnly: true },
    { path: '/ewaybill', label: 'E-way Bills', icon: Truck, adminOnly: true },
    { path: '/reports/stock-summary', label: 'Stock Summary', icon: BarChart3 },
    { path: '/reports/stock-matrix', label: 'Stock Matrix', icon: Grid3X3 },
  ];

  // Masters submenu (unchanged)
  const mastersNavItems = [
    { path: '/parties', label: 'Parties', icon: Building2, adminOnly: true },
    { path: '/brokers', label: 'Brokers', icon: Briefcase, adminOnly: true },
    { path: '/transporters', label: 'Transporters', icon: Truck, adminOnly: true },
    { path: '/grade-master', label: 'Sub-grade Master', icon: Layers, adminOnly: true },
    { path: '/settings', label: 'Company Settings', icon: Cog, adminOnly: true },
    { path: '/users', label: 'Users', icon: Users, adminOnly: true },
  ];

  // A) Back Office
  const backOfficeItems = [
    { path: '/proforma-entry', label: 'Proforma Invoice', icon: FilePlus2, adminOnly: true },
    { path: '/proforma-register', label: 'Proforma Register', icon: FileStack, adminOnly: true },
    { path: '/delivery-tracker', label: 'Document Tracker', icon: FileText, adminOnly: true },
    { path: '/challan-rates', label: 'Challan Rates & Charges', icon: Receipt, adminOnly: true },
    { path: '/test-certificates', label: 'Test Certificates', icon: FileCheck2, adminOnly: true },
    { path: '/automations', label: 'Automations', icon: Zap, superAdminOnly: true },
    { path: '/labourers', label: 'Labourers & WhatsApp AI', icon: Users, superAdminOnly: true },
  ];

  // B) Manage Inventory
  const manageInventoryItems = [
    { path: '/rm-processing', label: 'RM Processing', icon: Cog, adminOnly: true },
    { path: '/negative-stock', label: 'Negative Stock', icon: PackageX },
    { path: '/fix-stock', label: 'Fix Stock', icon: AlertTriangle },
    { path: '/missing-items', label: 'Missing Items', icon: PackageSearch },
    { path: '/found-items', label: 'Found Items', icon: PackagePlus },
    { path: '/stock-adjustment', label: 'Stock Adjustment', icon: Scale, adminOnly: true },
    { path: '/stock-reconciliation', label: 'Stock Reconciliation', icon: ClipboardCheck, adminOnly: true },
    { path: '/closing-differences', label: 'Closing Differences', icon: ClipboardList, superAdminOnly: true },
    { path: '/physical-verification', label: 'Physical Verification', icon: ClipboardList, adminOnly: true },
  ];

  // C) Rack Info & Reports
  const rackReportsItems = [
    { path: '/rack-view', label: 'Rack View', icon: Warehouse },
    { path: '/bulk-rack-update', label: 'Bulk Rack Update', icon: LayoutGrid, adminOnly: true },
    { path: '/items-without-rack', label: 'Items Without Rack', icon: PackageX, adminOnly: true },
    { path: '/stock', label: 'Stock View', icon: Package },
    { path: '/reports/item-wise', label: 'Item-Wise Report', icon: FileText },
  ];

  // D) Utilities
  const utilitiesItems = [
    { path: '/convert-to-transfer', label: 'Convert to Transfer', icon: ArrowRightLeft, adminOnly: true },
    { path: '/grade-cleanup', label: 'Stock Cleanup', icon: Trash2, adminOnly: true },
    { path: '/zero-qty-transactions', label: 'Zero-Qty Transactions', icon: AlertTriangle, adminOnly: true },
    { path: '/outward-without-challan', label: 'Outward w/o Challan', icon: FileText, adminOnly: true },
    { path: '/import/access', label: 'Import from Access', icon: DatabaseBackup, adminOnly: true },
    { path: '/data-management', label: 'Data Management', icon: Shield, superAdminOnly: true },
  ];

  const filterByRole = (items) => items.filter((item) => {
    if (item.superAdminOnly) return user?.role === 'superadmin';
    if (item.adminOnly) return isAdmin;
    return true;
  });

  const filteredMainNavItems = filterByRole(mainNavItems);

  // Collapsible menu sections config
  const collapsibleSections = [
    { key: 'backOffice', label: 'Back Office', icon: Briefcase, items: backOfficeItems, adminOnly: true },
    { key: 'manageInventory', label: 'Manage Stock', icon: Warehouse, items: manageInventoryItems },
    { key: 'rackReports', label: 'Rack & Reports', icon: MapPin, items: rackReportsItems },
    { key: 'utilities', label: 'Utilities', icon: Wrench, items: utilitiesItems, adminOnly: true },
    { key: 'masters', label: 'Masters', icon: Settings, items: mastersNavItems, adminOnly: true },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderSubmenu = (items) => {
    const filtered = filterByRole(items);
    if (filtered.length === 0) return null;
    return (
      <div className="ml-4 mt-1 space-y-1">
        {filtered.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              {...navProps(item.path)}
              className={`
                flex items-center gap-3 px-4 py-2 rounded-md text-sm font-medium transition-all
                ${active
                  ? 'bg-accent text-white shadow-sm'
                  : 'text-slate-500 hover:bg-slate-100'
                }
                ${item.superAdminOnly ? 'text-red-600 hover:bg-red-50' : ''}
              `}
            >
              <Icon size={18} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 to-slate-800 text-white sticky top-0 z-40 shadow-lg">
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden text-white hover:bg-slate-700 p-2 rounded-md transition-colors"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <Link to="/" className="hover:opacity-80 transition-opacity cursor-pointer">
              <h1 className="text-xl md:text-2xl font-bold tracking-tight">SteelFlow</h1>
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <PendingBadge />
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-slate-300 capitalize">{user?.role}</p>
            </div>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-slate-700"
            >
              <LogOut size={18} />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky top-[73px] left-0 h-[calc(100vh-73px)] supports-[height:100dvh]:h-[calc(100dvh-73px)] w-64
            bg-white border-r border-slate-200 shadow-sm overflow-y-auto overscroll-contain
            transform transition-transform duration-200 ease-in-out z-30
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          {/* pb-24 keeps the last items above the bottom tab bar */}
          <nav className="p-4 pb-24 space-y-2">
            {/* Main Navigation Items */}
            {filteredMainNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  {...navProps(item.path)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-md font-medium transition-all
                    ${active
                      ? 'bg-accent text-white shadow-sm'
                      : 'text-slate-600 hover:bg-slate-100'
                    }
                  `}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              );
            })}

            {/* Divider */}
            <div className="border-t border-slate-200 my-3"></div>

            {/* Collapsible Sections */}
            {collapsibleSections.map((section) => {
              if (section.adminOnly && !isAdmin) return null;
              const filtered = filterByRole(section.items);
              if (filtered.length === 0) return null;

              const SectionIcon = section.icon;
              const isSectionActive = filtered.some(item => isActive(item.path));

              return (
                <div key={section.key}>
                  <button
                    data-testid={`nav-${section.key}-toggle`}
                    onClick={() => toggleMenu(section.key)}
                    className={`
                      w-full flex items-center justify-between px-4 py-3 rounded-md font-medium transition-all
                      ${isSectionActive
                        ? 'bg-accent/10 text-accent'
                        : 'text-slate-600 hover:bg-slate-100'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <SectionIcon size={20} />
                      <span>{section.label}</span>
                    </div>
                    {menuOpen[section.key] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  {menuOpen[section.key] && renderSubmenu(section.items)}
                </div>
              );
            })}
          </nav>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-8 pb-20 min-w-0 overflow-x-hidden">
          <TabbedContent />
          {/* AI help from any page (English / Hindi / Gujarati) */}
          {location.pathname !== '/ai-assistant' && (
            <button
              type="button"
              onClick={() => openInNewTab('/ai-assistant')}
              className="fixed right-3 bottom-16 z-30 h-11 w-11 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg hover:scale-105 transition-transform flex items-center justify-center"
              title="Ask SteelFlow AI - English / हिंदी / ગુજરાતી"
              data-testid="ai-help-fab"
            >
              <Sparkles size={20} />
            </button>
          )}
        </main>
      </div>
      <TabBar />
    </div>
  );
};

export default Layout;
